import React, { ReactNode, useState, useEffect } from 'react';
import AdminSidebar from './navigation/AdminSidebar';
import { DashboardHeader } from '../../shared/components';

interface AdminLayoutProps {
    children: ReactNode;
    showHeader?: boolean;
    headerTitle?: string;
    headerSubtitle?: string;
    isHomePage?: boolean;
}

/**
 * Main layout component for admin dashboard pages
 * Includes sidebar navigation, shared DashboardHeader component
 */
const AdminLayout: React.FC<AdminLayoutProps> = ({
    children,
    showHeader = true,
    headerTitle,
    headerSubtitle,
    isHomePage = false
}) => {
    const [userName, setUserName] = useState('Admin');
    // Persist sidebar collapsed state in localStorage
    const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
        const saved = localStorage.getItem('adminSidebarCollapsed');
        return saved === 'true';
    });
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

    // Update localStorage when sidebar state changes
    const handleSidebarToggle = () => {
        const newState = !sidebarCollapsed;
        setSidebarCollapsed(newState);
        localStorage.setItem('adminSidebarCollapsed', String(newState));
    };

    // Get user name from localStorage
    useEffect(() => {
        const userDataString = localStorage.getItem('authUser');
        if (userDataString) {
            try {
                const userData = JSON.parse(userDataString);
                const firstName = userData.first_name || userData.firstName || userData.name || userData.email?.split('@')[0] || 'Admin';
                setUserName(firstName);
            } catch (error) {
                console.error('Failed to parse user data:', error);
            }
        }
    }, []);

    // Default header texts
    const defaultTitle = `Welcome back, ${userName}!`;
    const defaultSubtitle = "Here's what's happening on your platform today.";

    return (
        <div className="min-h-screen bg-[#F1F3F4] overflow-x-hidden" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {/* Sidebar */}
            <AdminSidebar
                isCollapsed={sidebarCollapsed}
                onToggle={handleSidebarToggle}
                isMobileOpen={mobileSidebarOpen}
                onMobileClose={() => setMobileSidebarOpen(false)}
            />

            {/* Mobile Menu Button - Fixed Position */}
            <button
                onClick={() => setMobileSidebarOpen(true)}
                className="lg:hidden fixed top-4 left-4 z-30 w-10 h-10 rounded-xl bg-white flex items-center justify-center hover:bg-gray-50 shadow-md border border-gray-200/80 transition-all duration-200 hover:shadow-lg"
            >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-[#015FA6]">
                    <line x1="3" x2="21" y1="6" y2="6" />
                    <line x1="3" x2="21" y1="12" y2="12" />
                    <line x1="3" x2="21" y1="18" y2="18" />
                </svg>
            </button>

            {/* Main Content Area */}
            <div className={`
                transition-all duration-300 pt-2 lg:pt-0
                ${sidebarCollapsed ? 'lg:ml-[100px]' : 'lg:ml-[280px]'}
            `}>
                {/* Shared Dashboard Header */}
                {showHeader && (
                    <div className="mb-2 lg:mb-4">
                        <DashboardHeader
                            title={headerTitle || defaultTitle}
                            subtitle={headerSubtitle || defaultSubtitle}
                            titleColor={isHomePage ? 'blue' : 'black'}
                        />
                    </div>
                )}

                {/* Page Content */}
                <main className="px-4 lg:px-8 pb-8 animate-fade-in-up">
                    {children}
                </main>
            </div>
        </div>
    );
};

export default AdminLayout;
